// src/cockpit4/components/RBACPanel.tsx
// ============================================
// RBAC 权限体系 - 设置中心 P0-功能1
//
// 功能（基于改进计划文档 10.2 功能1）：
//   1. 角色管理（自定义角色：管理员/经理/员工/只读等），系统内置角色不可删除
//   2. 权限粒度三级：模块级(module) / 功能级(function) / 数据级(data)
//   3. 数据权限：self(仅自己) / department(部门) / all(全部)
//   4. 权限配置矩阵（模块 × 操作 × 数据范围，复选框网格）
//   5. 用户角色分配（从现有用户中分配角色）
//   6. 权限申请与审批流程（用户申请 → 管理员审批）
//   7. 角色继承与组合（子角色继承父角色权限，逐级向上合并）
//   8. 权限审计日志（记录所有权限变更操作）
//
// 数据来源（白皮书铁律一：禁止假数据）：
//   - 角色 / 分配 / 申请 / 审计：localStorage(key: xeaos_rbac_config) 持久化
//   - 现有用户：useUserStore（../../stores/userStore），含超级管理员 + 员工列表
//   无数据时显示"暂无角色 / 点击创建"，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Crown,
  FileCheck,
  GitBranch,
  History,
  Inbox,
  Layers,
  Lock,
  Pencil,
  Plus,
  Save,
  ScrollText,
  Shield,
  ShieldCheck,
  Trash2,
  UserCog,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { useUserStore } from "../../stores/userStore";
import RuntimeManager from "../../runtime/RuntimeManager";

// ============================================
// 类型定义（与数据模型对齐）
// ============================================

/** 业务模块 */
type Module = 'ceo' | 'marketing' | 'media' | 'crm' | 'knowledge' | 'project' | 'finance' | 'workflow' | 'settings';

/** 权限粒度：模块级 / 功能级 / 数据级 */
type PermissionLevel = 'module' | 'function' | 'data';

/** 数据范围：个人 / 部门 / 全部 */
type DataScope = 'self' | 'department' | 'all';

/** 单条权限（按模块粒度） */
interface Permission {
  module: Module;
  level: PermissionLevel;
  actions: string[]; // 允许的操作：view / edit / delete / manage
  dataScope: DataScope;
}

/** 角色 */
interface Role {
  id: string;
  name: string;
  description: string;
  permissions: Permission[];
  parentRoleId?: string; // 父角色（继承）
  isSystem: boolean; // 系统内置角色不可删除
  createdAt: string;
  updatedAt: string;
}

/** 用户-角色分配记录 */
interface UserRoleAssignment {
  userId: string;
  username: string;
  roleId: string;
  assignedAt: string;
  assignedBy: string;
}

/** 权限申请 */
interface PermissionRequest {
  id: string;
  userId: string;
  username: string;
  roleId: string;
  roleName: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  handledAt?: string;
  handledBy?: string;
}

/** 权限审计日志 */
interface PermissionAuditLog {
  id: string;
  action: 'create_role' | 'edit_role' | 'delete_role' | 'assign_role' | 'revoke_role' | 'request_permission' | 'approve' | 'reject';
  operator: string;
  target: string;
  detail: string;
  timestamp: string;
}

/** RBAC 完整配置（持久化结构） */
interface RBACConfig {
  roles: Role[];
  assignments: UserRoleAssignment[];
  requests: PermissionRequest[];
  auditLogs: PermissionAuditLog[];
}

// ============================================
// 常量
// ============================================

const STORAGE_KEY = "xeaos_rbac_config";

/** 模块展示配置 */
const MODULE_CONFIG: Record<Module, { label: string; color: string }> = {
  ceo: { label: "CEO驾驶舱", color: "#FBBF24" },
  marketing: { label: "营销中心", color: "#F472B6" },
  media: { label: "媒体矩阵", color: "#A78BFA" },
  crm: { label: "客户CRM", color: "#34D399" },
  knowledge: { label: "知识库", color: "#60A5FA" },
  project: { label: "项目管理", color: "#FB923C" },
  finance: { label: "财务管理", color: "#F87171" },
  workflow: { label: "工作流", color: "#22D3EE" },
  settings: { label: "设置中心", color: "#9CA3AF" },
};

/** 全部模块（有序） */
const ALL_MODULES = Object.keys(MODULE_CONFIG) as Module[];

/** 操作展示配置 */
const ACTION_CONFIG: Record<string, { label: string; color: string }> = {
  view: { label: "查看", color: "#60A5FA" },
  edit: { label: "编辑", color: "#34D399" },
  delete: { label: "删除", color: "#F87171" },
  manage: { label: "管理", color: "#FBBF24" },
};

/** 全部操作（有序） */
const ALL_ACTIONS = ["view", "edit", "delete", "manage"];

/** 数据范围展示配置 */
const DATA_SCOPE_CONFIG: Record<DataScope, { label: string; color: string; desc: string }> = {
  self: { label: "仅自己", color: "#60A5FA", desc: "只能查看自己的数据" },
  department: { label: "本部门", color: "#FBBF24", desc: "可查看本部门数据" },
  all: { label: "全部", color: "#F87171", desc: "可查看全部数据" },
};

/** 权限粒度展示配置 */
const LEVEL_CONFIG: Record<PermissionLevel, { label: string; color: string; desc: string }> = {
  module: { label: "模块级", color: "#60A5FA", desc: "仅可访问模块（查看）" },
  function: { label: "功能级", color: "#34D399", desc: "可执行功能操作（编辑/删除）" },
  data: { label: "数据级", color: "#F87171", desc: "可管理并控制数据范围" },
};

/** 审计动作展示配置 */
const AUDIT_ACTION_CONFIG: Record<PermissionAuditLog["action"], { label: string; color: string }> = {
  create_role: { label: "创建角色", color: "#34D399" },
  edit_role: { label: "编辑角色", color: "#60A5FA" },
  delete_role: { label: "删除角色", color: "#F87171" },
  assign_role: { label: "分配角色", color: "#A78BFA" },
  revoke_role: { label: "撤销角色", color: "#FB923C" },
  request_permission: { label: "申请权限", color: "#FBBF24" },
  approve: { label: "审批通过", color: "#34D399" },
  reject: { label: "审批拒绝", color: "#F87171" },
};

/** 申请状态展示配置 */
const REQUEST_STATUS_CONFIG: Record<PermissionRequest["status"], { label: string; color: string; bg: string; border: string }> = {
  pending: { label: "待审批", color: "#FBBF24", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  approved: { label: "已通过", color: "#34D399", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  rejected: { label: "已拒绝", color: "#F87171", bg: "bg-red-500/10", border: "border-red-500/30" },
};

/** 系统内置角色规格 */
const SYSTEM_ROLE_SPECS = [
  { name: "超级管理员", scope: "all" as const, description: "拥有全部模块全部权限，可管理全部数据" },
  { name: "部门经理", scope: "department" as const, description: "可管理本部门数据，含查看/编辑/删除权限" },
  { name: "普通员工", scope: "self" as const, description: "仅可处理自己的数据，含查看/编辑权限" },
  { name: "只读用户", scope: "view_only" as const, description: "仅可查看自己数据，无任何写操作" },
];

/** Tab 类型 */
type RBACTab = "roles" | "assignments" | "requests" | "audit";

const TAB_CONFIG: Record<RBACTab, { label: string; icon: typeof Shield }> = {
  roles: { label: "角色管理", icon: Shield },
  assignments: { label: "用户分配", icon: UserCog },
  requests: { label: "申请审批", icon: FileCheck },
  audit: { label: "审计日志", icon: ScrollText },
};

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(prefix = "id"): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** ISO 时间转可读格式 MM-DD HH:MM */
function formatDateTime(iso?: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${mm}-${dd} ${hh}:${mi}`;
  } catch {
    return "—";
  }
}

/** 判断 ISO 时间是否为今天 */
function isToday(iso: string): boolean {
  try {
    const d = new Date(iso);
    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  } catch {
    return false;
  }
}

/** 根据操作与数据范围计算权限粒度 */
function computeLevel(actions: string[], dataScope: DataScope): PermissionLevel {
  const hasBeyondView = actions.some((a) => a !== "view");
  if (!hasBeyondView) return "module";
  if (dataScope !== "self") return "data";
  return "function";
}

/** 取两个数据范围中更宽的一个（all > department > self） */
function broaderScope(a: DataScope, b: DataScope): DataScope {
  const rank: Record<DataScope, number> = { self: 0, department: 1, all: 2 };
  return rank[a] >= rank[b] ? a : b;
}

/** 根据系统角色规格生成权限列表 */
function buildSystemPermissions(scope: "all" | "department" | "self" | "view_only"): Permission[] {
  // 非超级管理员不授予 settings 模块（安全基线）
  const modules = scope === "all" ? ALL_MODULES : ALL_MODULES.filter((m) => m !== "settings");
  if (scope === "view_only") {
    return modules.map((m) => ({
      module: m,
      level: "module" as PermissionLevel,
      actions: ["view"],
      dataScope: "self" as DataScope,
    }));
  }
  const dataScope: DataScope = scope;
  const actions = scope === "all" ? ["view", "edit", "delete", "manage"] : scope === "department" ? ["view", "edit", "delete"] : ["view", "edit"];
  return modules.map((m) => ({
    module: m,
    level: computeLevel(actions, dataScope),
    actions: [...actions],
    dataScope,
  }));
}

/** 初始化系统内置角色 */
function initSystemRoles(): Role[] {
  const now = new Date().toISOString();
  return SYSTEM_ROLE_SPECS.map((spec) => ({
    id: genId("role_sys"),
    name: spec.name,
    description: spec.description,
    permissions: buildSystemPermissions(spec.scope),
    isSystem: true,
    createdAt: now,
    updatedAt: now,
  }));
}

/** 读取 RBAC 配置（无则初始化系统角色） */
function loadConfig(): RBACConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as RBACConfig;
      return {
        roles: Array.isArray(parsed.roles) ? parsed.roles : [],
        assignments: Array.isArray(parsed.assignments) ? parsed.assignments : [],
        requests: Array.isArray(parsed.requests) ? parsed.requests : [],
        auditLogs: Array.isArray(parsed.auditLogs) ? parsed.auditLogs : [],
      };
    }
  } catch {
    // 损坏数据，回退到初始化
  }
  // 首次使用：初始化系统角色并记录一条审计
  const roles = initSystemRoles();
  const now = new Date().toISOString();
  const auditLogs: PermissionAuditLog[] = [
    {
      id: genId("aud"),
      action: "create_role",
      operator: "系统",
      target: "系统内置角色",
      detail: `初始化 ${roles.length} 个系统内置角色（超级管理员/部门经理/普通员工/只读用户）`,
      timestamp: now,
    },
  ];
  return { roles, assignments: [], requests: [], auditLogs };
}

/** 写入 RBAC 配置 */
function saveConfig(config: RBACConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // 容量超限或隐私模式，静默失败
  }
}

/**
 * 解析角色有效权限（含继承）
 * 自底向上沿 parentRoleId 链合并：父角色权限作为基底，子角色覆盖/扩展
 * 同模块合并时：操作取并集，数据范围取更宽者，粒度按合并结果重算
 */
function resolveEffectivePermissions(role: Role, roles: Role[]): Permission[] {
  const visited = new Set<string>();
  const chain: Role[] = [];
  let cur: Role | undefined = role;
  while (cur && !visited.has(cur.id)) {
    visited.add(cur.id);
    chain.push(cur);
    const parentId: string | undefined = cur.parentRoleId;
    cur = parentId ? roles.find((r) => r.id === parentId) : undefined;
  }
  // 从继承链顶端（最父）向子角色应用，子角色覆盖父角色
  const merged = new Map<Module, Permission>();
  for (let i = chain.length - 1; i >= 0; i--) {
    const r = chain[i];
    for (const p of r.permissions) {
      const existing = merged.get(p.module);
      if (!existing) {
        merged.set(p.module, { ...p, actions: [...p.actions] });
      } else {
        const mergedActions = Array.from(new Set([...existing.actions, ...p.actions]));
        const mergedScope = broaderScope(existing.dataScope, p.dataScope);
        merged.set(p.module, {
          module: p.module,
          actions: mergedActions,
          dataScope: mergedScope,
          level: computeLevel(mergedActions, mergedScope),
        });
      }
    }
  }
  return Array.from(merged.values());
}

/** 校验某角色（有效权限）是否具备指定模块/操作 */
function hasPermission(role: Role, roles: Role[], module: Module, action: string): boolean {
  const effective = resolveEffectivePermissions(role, roles);
  const p = effective.find((x) => x.module === module);
  return !!p && p.actions.includes(action);
}

// ============================================
// 子组件：KPI 卡片
// ============================================
interface MiniKpiProps {
  label: string;
  value: string | number;
  icon: typeof Shield;
  color: string;
  delay?: number;
}

function MiniKpi({ label, value, icon: Icon, color, delay = 0 }: MiniKpiProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: "easeOut" }}
      className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-3 overflow-hidden"
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${color}80, transparent)` }}
      />
      <div
        className="absolute -top-8 -right-8 w-16 h-16 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`, filter: "blur(16px)" }}
      />
      <div className="flex items-center gap-2 mb-1.5 relative">
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center"
          style={{ background: `${color}15`, border: `1px solid ${color}30` }}
        >
          <Icon size={12} style={{ color }} />
        </div>
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-[20px] font-mono font-bold text-white tracking-tight leading-none relative">
        {value}
      </div>
    </motion.div>
  );
}

// ============================================
// 子组件：空状态
// ============================================
function EmptyState({ text, sub }: { text: string; sub?: string }) {
  return (
    <div className="py-10 text-center">
      <div className="text-[12px] font-mono text-gray-600 italic">{text}</div>
      {sub && <div className="text-[10px] font-mono text-gray-700 mt-1.5">{sub}</div>}
    </div>
  );
}

// ============================================
// 子组件：权限粒度徽标
// ============================================
function LevelBadge({ level }: { level: PermissionLevel }) {
  const cfg = LEVEL_CONFIG[level];
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono border"
      style={{ color: cfg.color, borderColor: `${cfg.color}40`, background: `${cfg.color}15` }}
      title={cfg.desc}
    >
      {cfg.label}
    </span>
  );
}

// ============================================
// 子组件：数据范围徽标
// ============================================
function ScopeBadge({ scope }: { scope: DataScope }) {
  const cfg = DATA_SCOPE_CONFIG[scope];
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono border"
      style={{ color: cfg.color, borderColor: `${cfg.color}40`, background: `${cfg.color}15` }}
      title={cfg.desc}
    >
      {cfg.label}
    </span>
  );
}

// ============================================
// 子组件：权限矩阵（只读，展示有效/自有权限）
// ============================================
function PermissionMatrixView({ permissions, title, inherited = false }: { permissions: Permission[]; title: string; inherited?: boolean }) {
  if (permissions.length === 0) {
    return (
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-2.5">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Layers size={11} className="text-gray-500" />
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">{title}</span>
        </div>
        <div className="text-[10px] font-mono text-gray-600 italic">未配置任何权限</div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-2.5">
      <div className="flex items-center gap-1.5 mb-2">
        <Layers size={11} className={inherited ? "text-violet-400" : "text-gray-400"} />
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">{title}</span>
        {inherited && (
          <span className="inline-flex items-center gap-0.5 text-[9px] font-mono text-violet-400">
            <GitBranch size={9} /> 含继承
          </span>
        )}
      </div>
      <div className="space-y-1">
        {permissions.map((p) => {
          const cfg = MODULE_CONFIG[p.module];
          return (
            <div key={p.module} className="flex items-center gap-2 py-1 px-1.5 rounded hover:bg-white/[0.02]">
              <span className="text-[10px] font-mono w-16 shrink-0 truncate" style={{ color: cfg.color }} title={cfg.label}>
                {cfg.label}
              </span>
              <div className="flex-1 flex flex-wrap gap-1">
                {p.actions.map((a) => {
                  const ac = ACTION_CONFIG[a];
                  return (
                    <span
                      key={a}
                      className="px-1 py-0.5 rounded text-[9px] font-mono border"
                      style={{ color: ac.color, borderColor: `${ac.color}30`, background: `${ac.color}10` }}
                    >
                      {ac.label}
                    </span>
                  );
                })}
              </div>
              <ScopeBadge scope={p.dataScope} />
              <LevelBadge level={p.level} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// 子组件：权限矩阵编辑器（模块 × 操作 × 数据范围 复选框网格）
// ============================================
type MatrixCell = { actions: string[]; dataScope: DataScope };
type MatrixState = Partial<Record<Module, MatrixCell>>;

function PermissionMatrixEditor({ value, onChange }: { value: MatrixState; onChange: (next: MatrixState) => void }) {
  /** 切换某模块某操作 */
  const toggleAction = (mod: Module, action: string) => {
    const cell = value[mod] || { actions: [], dataScope: "self" as DataScope };
    const has = cell.actions.includes(action);
    const nextActions = has ? cell.actions.filter((a) => a !== action) : [...cell.actions, action];
    const nextCell: MatrixCell = { ...cell, actions: nextActions, dataScope: cell.dataScope };
    onChange({ ...value, [mod]: nextCell });
  };

  /** 设置某模块数据范围 */
  const setScope = (mod: Module, scope: DataScope) => {
    const cell = value[mod] || { actions: [], dataScope: "self" as DataScope };
    onChange({ ...value, [mod]: { ...cell, dataScope: scope } });
  };

  /** 一键全选某模块 */
  const toggleAll = (mod: Module) => {
    const cell = value[mod];
    const allSelected = cell && ALL_ACTIONS.every((a) => cell.actions.includes(a));
    if (allSelected) {
      onChange({ ...value, [mod]: { actions: [], dataScope: cell?.dataScope || "self" } });
    } else {
      onChange({ ...value, [mod]: { actions: [...ALL_ACTIONS], dataScope: cell?.dataScope || "all" } });
    }
  };

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] overflow-hidden">
      {/* 表头 */}
      <div className="grid grid-cols-[100px_1fr_140px_70px] gap-2 px-2 py-1.5 border-b border-white/[0.06] bg-white/[0.02]">
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">模块</span>
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">操作（复选框网格）</span>
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">数据范围</span>
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">粒度</span>
      </div>
      {/* 行 */}
      <div className="max-h-[280px] overflow-y-auto">
        {ALL_MODULES.map((mod) => {
          const cfg = MODULE_CONFIG[mod];
          const cell = value[mod] || { actions: [], dataScope: "self" as DataScope };
          const level = computeLevel(cell.actions, cell.dataScope);
          return (
            <div key={mod} className="grid grid-cols-[100px_1fr_140px_70px] gap-2 px-2 py-1.5 border-b border-white/[0.04] items-center hover:bg-white/[0.02]">
              <button
                onClick={() => toggleAll(mod)}
                className="text-left text-[11px] font-mono truncate hover:underline"
                style={{ color: cfg.color }}
                title={`点击全选/清空 ${cfg.label}`}
              >
                {cfg.label}
              </button>
              <div className="flex flex-wrap gap-1.5">
                {ALL_ACTIONS.map((a) => {
                  const ac = ACTION_CONFIG[a];
                  const checked = cell.actions.includes(a);
                  return (
                    <button
                      key={a}
                      onClick={() => toggleAction(mod, a)}
                      className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono border transition-colors ${
                        checked
                          ? "text-white"
                          : "text-gray-500 border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
                      }`}
                      style={
                        checked
                          ? { color: ac.color, borderColor: `${ac.color}50`, background: `${ac.color}18` }
                          : undefined
                      }
                    >
                      <span
                        className="w-3 h-3 rounded-sm flex items-center justify-center border"
                        style={{
                          borderColor: checked ? `${ac.color}80` : "rgba(255,255,255,0.15)",
                          background: checked ? `${ac.color}30` : "transparent",
                        }}
                      >
                        {checked && <Check size={8} style={{ color: ac.color }} />}
                      </span>
                      {ac.label}
                    </button>
                  );
                })}
              </div>
              <select
                value={cell.dataScope}
                onChange={(e) => setScope(mod, e.target.value as DataScope)}
                className="rounded-md border border-white/[0.06] bg-white/[0.02] px-1.5 py-1 text-[10px] text-gray-300 font-mono outline-none focus:border-violet-500/40"
              >
                <option value="self">仅自己</option>
                <option value="department">本部门</option>
                <option value="all">全部</option>
              </select>
              <LevelBadge level={level} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// 子组件：角色创建/编辑弹窗
// ============================================
interface RoleModalProps {
  role: Role | null; // null 表示新建
  roles: Role[]; // 可选父角色列表（排除自身及其后代，避免继承环）
  me: string;
  onClose: () => void;
  onSubmit: (data: { name: string; description: string; parentRoleId?: string; permissions: Permission[] }) => void;
}

function RoleModal({ role, roles, me, onClose, onSubmit }: RoleModalProps) {
  const [name, setName] = useState(role?.name || "");
  const [description, setDescription] = useState(role?.description || "");
  const [parentRoleId, setParentRoleId] = useState<string>(role?.parentRoleId || "");

  // 初始化矩阵：从已有 permissions 转换为 MatrixState
  const [matrix, setMatrix] = useState<MatrixState>(() => {
    const state: MatrixState = {};
    if (role?.permissions) {
      for (const p of role.permissions) {
        state[p.module] = { actions: [...p.actions], dataScope: p.dataScope };
      }
    }
    return state;
  });

  /** 收集自身后代 ID（避免继承环：不能选择自己或自己的后代作为父角色） */
  const descendantIds = useMemo(() => {
    const ids = new Set<string>();
    if (!role) return ids;
    const collect = (parentId: string) => {
      for (const r of roles) {
        if (r.parentRoleId === parentId) {
          ids.add(r.id);
          collect(r.id);
        }
      }
    };
    ids.add(role.id);
    collect(role.id);
    return ids;
  }, [role, roles]);

  // 可选父角色：非系统角色自身、非自身后代
  const parentCandidates = roles.filter((r) => !descendantIds.has(r.id));

  const handleSubmit = () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    // 将矩阵转换为 Permission[]（仅保留有操作的模块）
    const permissions: Permission[] = [];
    for (const mod of ALL_MODULES) {
      const cell = matrix[mod];
      if (cell && cell.actions.length > 0) {
        permissions.push({
          module: mod,
          level: computeLevel(cell.actions, cell.dataScope),
          actions: [...cell.actions],
          dataScope: cell.dataScope,
        });
      }
    }
    onSubmit({
      name: trimmedName,
      description: description.trim(),
      parentRoleId: parentRoleId || undefined,
      permissions,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl border border-white/[0.06] bg-[#0a0a0f]/95 backdrop-blur-xl p-4 space-y-3"
      >
        {/* 顶部光带 */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500/60 to-transparent" />

        {/* 标题栏 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} className="text-violet-400" />
            <span className="text-[13px] font-medium text-white">
              {role ? "编辑角色" : "创建角色"}
            </span>
            {role?.isSystem && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono text-amber-300 border border-amber-500/30 bg-amber-500/10">
                <Crown size={9} /> 系统角色
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded text-gray-500 hover:text-gray-300 hover:bg-white/[0.04] transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 角色名称 */}
        <div>
          <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">角色名称</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="如：内容运营经理"
            disabled={role?.isSystem}
            className="w-full mt-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 text-[12px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-violet-500/40 disabled:opacity-60"
          />
        </div>

        {/* 角色描述 */}
        <div>
          <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">角色描述</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="该角色的职责与权限范围说明"
            disabled={role?.isSystem}
            className="w-full mt-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 text-[12px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-violet-500/40 disabled:opacity-60"
          />
        </div>

        {/* 继承父角色 */}
        <div>
          <label className="flex items-center gap-1 text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            <GitBranch size={11} /> 继承父角色（可选，子角色继承父角色权限）
          </label>
          <select
            value={parentRoleId}
            onChange={(e) => setParentRoleId(e.target.value)}
            disabled={role?.isSystem}
            className="w-full mt-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 text-[12px] text-gray-200 font-mono outline-none focus:border-violet-500/40 disabled:opacity-60"
          >
            <option value="">不继承（独立角色）</option>
            {parentCandidates.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}{r.isSystem ? "（系统）" : ""}
              </option>
            ))}
          </select>
        </div>

        {/* 权限配置矩阵 */}
        <div>
          <label className="flex items-center gap-1 text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            <Layers size={11} /> 权限配置矩阵（模块 × 操作 × 数据范围）
          </label>
          <div className="mt-1.5">
            <PermissionMatrixEditor value={matrix} onChange={setMatrix} />
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-[11px] font-mono text-gray-400 border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name.trim()}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-mono text-white border border-violet-500/40 bg-violet-500/20 hover:bg-violet-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save size={11} />
            {role ? "保存修改" : "创建角色"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 子组件：权限申请弹窗
// ============================================
interface RequestModalProps {
  roles: Role[];
  me: string;
  meUsername: string;
  onClose: () => void;
  onSubmit: (data: { roleId: string; roleName: string; reason: string }) => void;
}

function RequestModal({ roles, me, meUsername, onClose, onSubmit }: RequestModalProps) {
  const [roleId, setRoleId] = useState<string>("");
  const [reason, setReason] = useState<string>("");

  const handleSubmit = () => {
    if (!roleId || !reason.trim()) return;
    const role = roles.find((r) => r.id === roleId);
    if (!role) return;
    onSubmit({ roleId, roleName: role.name, reason: reason.trim() });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl border border-white/[0.06] bg-[#0a0a0f]/95 backdrop-blur-xl p-4 space-y-3"
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileCheck size={14} className="text-amber-400" />
            <span className="text-[13px] font-medium text-white">申请角色权限</span>
          </div>
          <button onClick={onClose} className="p-1 rounded text-gray-500 hover:text-gray-300 hover:bg-white/[0.04] transition-colors">
            <X size={14} />
          </button>
        </div>

        <div>
          <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">申请角色</label>
          <select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            className="w-full mt-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 text-[12px] text-gray-200 font-mono outline-none focus:border-amber-500/40"
          >
            <option value="">请选择角色</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}{r.isSystem ? "（系统）" : ""}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">申请理由</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="说明申请该角色的业务原因"
            rows={3}
            className="w-full mt-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 text-[12px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-amber-500/40 resize-none"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-[11px] font-mono text-gray-400 border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!roleId || !reason.trim()}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-mono text-white border border-amber-500/40 bg-amber-500/20 hover:bg-amber-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileCheck size={11} />
            提交申请
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 主组件
// ============================================
export default function RBACPanel() {
  // 当前用户
  const currentUser = useUserStore((s) => s.currentUser);
  const listEmployees = useUserStore((s) => s.listEmployees);
  const me = currentUser?.displayName || currentUser?.username || "系统";
  const meUsername = currentUser?.username || "";
  // 仅超级管理员可执行管理类操作（与 userStore 权限模型一致）
  const canManage = currentUser?.role === "super_admin";

  // ===== v5.2 审计修复 P3-⑦：权限检查开关（默认关闭，启用需明确操作） =====
  const [authOn, setAuthOn] = useState<boolean>(() => {
    try {
      return localStorage.getItem("xeaos_auth_enabled") === "true";
    } catch { return false; }
  });
  const toggleAuth = () => {
    const next = !authOn;
    setAuthOn(next);
    try {
      localStorage.setItem("xeaos_auth_enabled", next ? "true" : "false");
      if (next) {
        (RuntimeManager as any).enableAuth?.();
      } else {
        (RuntimeManager as any).disableAuth?.();
      }
    } catch { /* 忽略 */ }
  };


  // ===== 状态 =====
  const [config, setConfig] = useState<RBACConfig>({ roles: [], assignments: [], requests: [], auditLogs: [] });
  const [activeTab, setActiveTab] = useState<RBACTab>("roles");
  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null);
  const [showRoleModal, setShowRoleModal] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [showRequestModal, setShowRequestModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  // ===== 初始化加载 =====
  useEffect(() => {
    setConfig(loadConfig());
    setLoading(false);
  }, []);

  // ===== 持久化 =====
  const persist = useCallback((next: RBACConfig) => {
    setConfig(next);
    saveConfig(next);
  }, []);

  /** 追加审计日志 */
  const appendAudit = useCallback(
    (
      prev: RBACConfig,
      action: PermissionAuditLog["action"],
      target: string,
      detail: string
    ): RBACConfig => {
      const log: PermissionAuditLog = {
        id: genId("aud"),
        action,
        operator: me,
        target,
        detail,
        timestamp: new Date().toISOString(),
      };
      return { ...prev, auditLogs: [log, ...prev.auditLogs] };
    },
    [me]
  );

  // ===== 现有用户列表（来自 useUserStore，禁止假数据） =====
  const employees = listEmployees();
  const users = useMemo(() => {
    const list: Array<{ username: string; displayName: string; isSuperAdmin: boolean }> = [];
    if (currentUser) {
      list.push({
        username: currentUser.username,
        displayName: currentUser.displayName,
        isSuperAdmin: currentUser.role === "super_admin",
      });
    }
    for (const e of employees) {
      list.push({ username: e.username, displayName: e.displayName, isSuperAdmin: false });
    }
    return list;
  }, [currentUser, employees]);

  // ===== 角色操作 =====
  const handleCreateRole = useCallback(
    (data: { name: string; description: string; parentRoleId?: string; permissions: Permission[] }) => {
      const now = new Date().toISOString();
      const role: Role = {
        id: genId("role"),
        name: data.name,
        description: data.description,
        permissions: data.permissions,
        parentRoleId: data.parentRoleId,
        isSystem: false,
        createdAt: now,
        updatedAt: now,
      };
      let next: RBACConfig = { ...config, roles: [...config.roles, role] };
      next = appendAudit(next, "create_role", data.name, `创建角色「${data.name}」，含 ${data.permissions.length} 个模块权限${data.parentRoleId ? `，继承父角色` : ""}`);
      persist(next);
      setShowRoleModal(false);
      setEditingRole(null);
      setExpandedRoleId(role.id);
    },
    [config, persist, appendAudit]
  );

  const handleEditRole = useCallback(
    (data: { name: string; description: string; parentRoleId?: string; permissions: Permission[] }) => {
      if (!editingRole) return;
      const now = new Date().toISOString();
      const next: RBACConfig = {
        ...config,
        roles: config.roles.map((r) =>
          r.id === editingRole.id
            ? {
                ...r,
                name: editingRole.isSystem ? r.name : data.name,
                description: data.description,
                permissions: data.permissions,
                parentRoleId: data.parentRoleId,
                updatedAt: now,
              }
            : r
        ),
      };
      const audited = appendAudit(next, "edit_role", editingRole.name, `编辑角色「${editingRole.name}」权限配置，含 ${data.permissions.length} 个模块权限`);
      persist(audited);
      setShowRoleModal(false);
      setEditingRole(null);
    },
    [config, editingRole, persist, appendAudit]
  );

  const handleDeleteRole = useCallback(
    (role: Role) => {
      if (role.isSystem) return; // 系统角色不可删除
      // 同步清理：分配记录 + 申请记录，并清理以该角色为父的继承关系
      const cleaned: RBACConfig = {
        ...config,
        roles: config.roles
          .filter((r) => r.id !== role.id)
          .map((r) => (r.parentRoleId === role.id ? { ...r, parentRoleId: undefined } : r)),
        assignments: config.assignments.filter((a) => a.roleId !== role.id),
        requests: config.requests.filter((q) => q.roleId !== role.id),
      };
      const audited = appendAudit(cleaned, "delete_role", role.name, `删除角色「${role.name}」，并清理相关分配与申请记录`);
      persist(audited);
      if (expandedRoleId === role.id) setExpandedRoleId(null);
    },
    [config, persist, appendAudit, expandedRoleId]
  );

  // ===== 用户角色分配 =====
  const handleAssignRole = useCallback(
    (username: string, displayName: string, roleId: string) => {
      const role = config.roles.find((r) => r.id === roleId);
      if (!role) return;
      const now = new Date().toISOString();
      const existing = config.assignments.find((a) => a.username === username);
      let nextAssignments: UserRoleAssignment[];
      let action: PermissionAuditLog["action"];
      let detail: string;
      if (!roleId) {
        // 撤销角色
        nextAssignments = config.assignments.filter((a) => a.username !== username);
        action = "revoke_role";
        detail = `撤销用户「${displayName}」的角色分配`;
      } else if (existing) {
        nextAssignments = config.assignments.map((a) =>
          a.username === username
            ? { ...a, roleId, assignedAt: now, assignedBy: me }
            : a
        );
        action = "assign_role";
        detail = `将用户「${displayName}」的角色变更为「${role.name}」`;
      } else {
        nextAssignments = [
          ...config.assignments,
          {
            userId: username,
            username,
            roleId,
            assignedAt: now,
            assignedBy: me,
          },
        ];
        action = "assign_role";
        detail = `为用户「${displayName}」分配角色「${role.name}」`;
      }
      let next: RBACConfig = { ...config, assignments: nextAssignments };
      next = appendAudit(next, action, displayName, detail);
      persist(next);
    },
    [config, me, persist, appendAudit]
  );

  // ===== 权限申请 =====
  const handleCreateRequest = useCallback(
    (data: { roleId: string; roleName: string; reason: string }) => {
      const req: PermissionRequest = {
        id: genId("req"),
        userId: meUsername,
        username: me,
        roleId: data.roleId,
        roleName: data.roleName,
        reason: data.reason,
        status: "pending",
        requestedAt: new Date().toISOString(),
      };
      let next: RBACConfig = { ...config, requests: [req, ...config.requests] };
      next = appendAudit(next, "request_permission", data.roleName, `${me} 申请角色「${data.roleName}」：${data.reason}`);
      persist(next);
      setShowRequestModal(false);
    },
    [config, me, meUsername, persist, appendAudit]
  );

  const handleApproveRequest = useCallback(
    (req: PermissionRequest, approved: boolean) => {
      const now = new Date().toISOString();
      const next: RBACConfig = {
        ...config,
        requests: config.requests.map((q) =>
          q.id === req.id
            ? {
                ...q,
                status: approved ? "approved" : "rejected",
                handledAt: now,
                handledBy: me,
              }
            : q
        ),
      };
      // 审批通过：自动为申请用户分配对应角色
      let finalNext = next;
      if (approved) {
        const existing = next.assignments.find((a) => a.username === req.username);
        let assignments: UserRoleAssignment[];
        if (existing) {
          assignments = next.assignments.map((a) =>
            a.username === req.username ? { ...a, roleId: req.roleId, assignedAt: now, assignedBy: me } : a
          );
        } else {
          assignments = [
            ...next.assignments,
            { userId: req.userId, username: req.username, roleId: req.roleId, assignedAt: now, assignedBy: me },
          ];
        }
        finalNext = { ...next, assignments };
      }
      const audited = appendAudit(
        finalNext,
        approved ? "approve" : "reject",
        req.roleName,
        `${approved ? "通过" : "拒绝"}「${req.username}」申请角色「${req.roleName}」`
      );
      persist(audited);
    },
    [config, me, persist, appendAudit]
  );

  // ===== 统计 =====
  const stats = useMemo(() => {
    const roleCount = config.roles.length;
    const userCount = users.length;
    const pendingCount = config.requests.filter((q) => q.status === "pending").length;
    const todayChangeCount = config.auditLogs.filter((l) => isToday(l.timestamp)).length;
    return { roleCount, userCount, pendingCount, todayChangeCount };
  }, [config.roles, config.requests, config.auditLogs, users.length]);

  const hasRoles = config.roles.length > 0;

  // ===== 渲染 =====
  return (
    <div className="space-y-3">
      {/* ===== 顶部标题栏 ===== */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-violet-400" />
          <span className="text-[14px] font-medium text-white">RBAC 权限体系</span>
          <span className="text-[10px] font-mono text-gray-600">
            {loading ? "加载中" : hasRoles ? `${config.roles.length} 个角色` : "本地模式"}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {/* v5.2 审计修复 P3-⑦：权限检查总开关（默认关闭，避免阻塞单用户闭环；启用后 Runtime 调用走权限拦截） */}
          <button
            onClick={toggleAuth}
            title="启用后，Runtime 查询/执行将按用户角色做权限拦截（多用户场景建议开启）"
            className={`flex items-center gap-1 px-2 py-1 rounded-md border transition-colors ${
              authOn
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                : "border-white/[0.1] bg-white/[0.03] text-gray-500"
            }`}
          >
            <ShieldCheck size={11} className={authOn ? "text-emerald-400" : ""} />
            <span className="text-[11px] font-mono">权限检查：{authOn ? "已启用" : "已停用"}</span>
          </button>
          {activeTab === "requests" && (
            <button
              onClick={() => setShowRequestModal(true)}
              className="flex items-center gap-1 px-2 py-1 rounded-md border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 transition-colors"
            >
              <FileCheck size={11} className="text-amber-400" />
              <span className="text-[11px] font-mono text-amber-300">申请角色</span>
            </button>
          )}
          {canManage && (activeTab === "roles" || activeTab === "assignments") && (
            <button
              onClick={() => {
                setEditingRole(null);
                setShowRoleModal(true);
              }}
              className="flex items-center gap-1 px-2 py-1 rounded-md border border-violet-500/30 bg-violet-500/10 hover:bg-violet-500/20 transition-colors"
            >
              <Plus size={11} className="text-violet-400" />
              <span className="text-[11px] font-mono text-violet-300">创建角色</span>
            </button>
          )}
        </div>
      </div>

      {/* ===== KPI 卡片区 ===== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <MiniKpi label="角色数" value={stats.roleCount} icon={Shield} color="#A78BFA" delay={0} />
        <MiniKpi label="用户数" value={stats.userCount} icon={Users} color="#60A5FA" delay={0.05} />
        <MiniKpi label="待审批" value={stats.pendingCount} icon={Inbox} color="#FBBF24" delay={0.1} />
        <MiniKpi label="今日变更" value={stats.todayChangeCount} icon={History} color="#34D399" delay={0.15} />
      </div>

      {/* ===== Tab 切换 ===== */}
      <div className="flex items-center gap-1 border-b border-white/[0.06]">
        {(Object.keys(TAB_CONFIG) as RBACTab[]).map((tab) => {
          const cfg = TAB_CONFIG[tab];
          const active = activeTab === tab;
          const badge =
            tab === "requests" ? config.requests.filter((q) => q.status === "pending").length : 0;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`relative flex items-center gap-1.5 px-3 py-1.5 transition-colors ${
                active ? "text-white" : "text-gray-500 hover:text-gray-300"
              }`}
            >
              <cfg.icon size={12} />
              <span className="text-[11px] font-mono">{cfg.label}</span>
              {badge > 0 && (
                <span className="ml-0.5 px-1 py-0.5 rounded-full text-[9px] font-mono text-amber-300 bg-amber-500/15 border border-amber-500/30">
                  {badge}
                </span>
              )}
              {active && (
                <motion.div
                  layoutId="rbac-tab-underline"
                  className="absolute bottom-0 left-0 right-0 h-px bg-violet-400"
                />
              )}
            </button>
          );
        })}
      </div>

      {/* ===== Tab 内容 ===== */}
      {/* Tab1 角色管理 */}
      {activeTab === "roles" && (
        <div className="space-y-1.5">
          {!hasRoles ? (
            <EmptyState text="暂无角色 / 点击创建" sub="点击右上角「创建角色」添加第一个自定义角色" />
          ) : (
            <AnimatePresence initial={false}>
              {config.roles.map((role) => {
                const isExpanded = expandedRoleId === role.id;
                const parentRole = role.parentRoleId
                  ? config.roles.find((r) => r.id === role.parentRoleId)
                  : null;
                const effective = resolveEffectivePermissions(role, config.roles);
                const assignCount = config.assignments.filter((a) => a.roleId === role.id).length;
                return (
                  <motion.div
                    key={role.id}
                    layout
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.25 }}
                    className={`rounded-lg border backdrop-blur-md overflow-hidden transition-colors ${
                      isExpanded ? "border-violet-500/40 bg-violet-500/[0.03]" : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
                    }`}
                  >
                    {/* 角色行 */}
                    <div
                      className="flex items-center gap-2 p-2.5 cursor-pointer"
                      onClick={() => setExpandedRoleId(isExpanded ? null : role.id)}
                    >
                      {isExpanded ? (
                        <ChevronDown size={12} className="text-gray-400 shrink-0" />
                      ) : (
                        <ChevronRight size={12} className="text-gray-500 shrink-0" />
                      )}
                      <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: role.isSystem ? "#FBBF2415" : "#A78BFA15", border: `1px solid ${role.isSystem ? "#FBBF2430" : "#A78BFA30"}` }}>
                        {role.isSystem ? <Crown size={13} className="text-amber-400" /> : <Shield size={13} className="text-violet-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[12px] font-medium text-white truncate">{role.name}</span>
                          {role.isSystem && (
                            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-mono text-amber-300 border border-amber-500/30 bg-amber-500/10">
                              <Lock size={8} /> 系统
                            </span>
                          )}
                          {parentRole && (
                            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-mono text-violet-300 border border-violet-500/30 bg-violet-500/10" title={`继承自 ${parentRole.name}`}>
                              <GitBranch size={8} /> 继承 {parentRole.name}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] font-mono text-gray-500 truncate mt-0.5">
                          {role.description || "无描述"} · {role.permissions.length} 模块 · {assignCount} 用户
                        </div>
                      </div>
                      {/* 操作按钮 */}
                      {canManage && (
                        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => {
                              setEditingRole(role);
                              setShowRoleModal(true);
                            }}
                            className="p-1 rounded text-gray-400 hover:text-violet-300 hover:bg-violet-500/10 transition-colors"
                            title="编辑角色"
                          >
                            <Pencil size={11} />
                          </button>
                          {!role.isSystem && (
                            <button
                              onClick={() => handleDeleteRole(role)}
                              className="p-1 rounded text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                              title="删除角色"
                            >
                              <Trash2 size={11} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* 展开内容：权限配置矩阵 */}
                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="px-2.5 pb-2.5 space-y-2 overflow-hidden"
                        >
                          {parentRole ? (
                            <>
                              <PermissionMatrixView permissions={role.permissions} title="自有权限" />
                              <PermissionMatrixView permissions={effective} title="有效权限（含继承）" inherited />
                            </>
                          ) : (
                            <PermissionMatrixView permissions={effective} title="权限配置" />
                          )}
                          {/* 权限生效验证：抽样校验各模块操作 */}
                          <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-2.5">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <ShieldCheck size={11} className="text-emerald-400" />
                              <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">权限生效验证</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {ALL_MODULES.slice(0, 5).map((m) => {
                                const canView = hasPermission(role, config.roles, m, "view");
                                const canManageMod = hasPermission(role, config.roles, m, "manage");
                                const cfg = MODULE_CONFIG[m];
                                return (
                                  <span
                                    key={m}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono border"
                                    style={{
                                      color: canView ? cfg.color : "#4B5563",
                                      borderColor: canView ? `${cfg.color}30` : "rgba(255,255,255,0.06)",
                                      background: canView ? `${cfg.color}10` : "transparent",
                                    }}
                                    title={`${cfg.label}：查看${canView ? "✓" : "✗"} / 管理${canManageMod ? "✓" : "✗"}`}
                                  >
                                    {cfg.label}
                                    {canView ? <Check size={8} /> : <X size={8} />}
                                    {canManageMod && <Crown size={8} />}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      )}

      {/* Tab2 用户分配 */}
      {activeTab === "assignments" && (
        <div className="space-y-1.5">
          {users.length === 0 ? (
            <EmptyState text="暂无用户" sub="用户数据来自 useUserStore，请先在系统中添加员工" />
          ) : (
            users.map((u) => {
              const assignment = config.assignments.find((a) => a.username === u.username);
              const assignedRole = assignment
                ? config.roles.find((r) => r.id === assignment.roleId)
                : null;
              return (
                <div
                  key={u.username}
                  className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-2.5"
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 bg-white/[0.04] border border-white/[0.06]">
                    {u.isSuperAdmin ? <Crown size={13} className="text-amber-400" /> : <Users size={13} className="text-gray-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[12px] font-medium text-white truncate">{u.displayName}</span>
                      {u.isSuperAdmin && (
                        <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-mono text-amber-300 border border-amber-500/30 bg-amber-500/10">
                          超管
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-gray-500 truncate">
                      @{u.username}
                      {assignedRole && (
                        <span className="ml-1.5 text-gray-400">
                          · 当前: <span style={{ color: assignedRole.isSystem ? "#FBBF24" : "#A78BFA" }}>{assignedRole.name}</span>
                        </span>
                      )}
                      {assignment && (
                        <span className="ml-1.5 text-gray-600">· 分配于 {formatDateTime(assignment.assignedAt)}</span>
                      )}
                    </div>
                  </div>
                  {/* 角色分配下拉 */}
                  {canManage ? (
                    <select
                      value={assignment?.roleId || ""}
                      onChange={(e) => handleAssignRole(u.username, u.displayName, e.target.value)}
                      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1 text-[11px] text-gray-300 font-mono outline-none focus:border-violet-500/40 shrink-0"
                    >
                      <option value="">未分配</option>
                      {config.roles.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}{r.isSystem ? "（系统）" : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-[11px] font-mono text-gray-500 shrink-0">
                      {assignedRole ? assignedRole.name : "未分配"}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Tab3 申请审批 */}
      {activeTab === "requests" && (
        <div className="space-y-1.5">
          {config.requests.length === 0 ? (
            <EmptyState text="暂无权限申请" sub="点击右上角「申请角色」发起一条权限申请" />
          ) : (
            config.requests.map((req) => {
              const cfg = REQUEST_STATUS_CONFIG[req.status];
              const canHandle = req.status === "pending" && canManage;
              const isMine = req.userId === meUsername;
              return (
                <div
                  key={req.id}
                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-2.5"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 bg-amber-500/10 border border-amber-500/30">
                      <FileCheck size={13} className="text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[12px] font-medium text-white truncate">
                          {req.username}
                          {isMine && <span className="ml-1 text-[10px] font-mono text-violet-400">（我）</span>}
                        </span>
                        <span className="text-[10px] font-mono text-gray-500">申请</span>
                        <span className="text-[12px] font-medium text-amber-300 truncate">{req.roleName}</span>
                      </div>
                      <div className="text-[10px] font-mono text-gray-500 truncate mt-0.5">
                        理由：{req.reason} · {formatDateTime(req.requestedAt)}
                        {req.handledAt && req.handledBy && (
                          <span className="ml-1.5 text-gray-600">· {req.handledBy} 于 {formatDateTime(req.handledAt)} 处理</span>
                        )}
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono border ${cfg.bg} ${cfg.border}`} style={{ color: cfg.color }}>
                      {cfg.label}
                    </span>
                  </div>
                  {canHandle && (
                    <div className="flex items-center justify-end gap-1.5 mt-2 pt-2 border-t border-white/[0.06]">
                      <button
                        onClick={() => handleApproveRequest(req, false)}
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono text-red-300 border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 transition-colors"
                      >
                        <XCircle size={11} /> 拒绝
                      </button>
                      <button
                        onClick={() => handleApproveRequest(req, true)}
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors"
                      >
                        <CheckCircle2 size={11} /> 通过
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Tab4 审计日志 */}
      {activeTab === "audit" && (
        <div className="space-y-1.5">
          {config.auditLogs.length === 0 ? (
            <EmptyState text="暂无审计日志" sub="所有权限变更操作将自动记录于此" />
          ) : (
            config.auditLogs.map((log) => {
              const cfg = AUDIT_ACTION_CONFIG[log.action];
              return (
                <div
                  key={log.id}
                  className="flex items-start gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-2.5"
                >
                  <div
                    className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: `${cfg.color}15`, border: `1px solid ${cfg.color}30` }}
                  >
                    <History size={13} style={{ color: cfg.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono border"
                        style={{ color: cfg.color, borderColor: `${cfg.color}40`, background: `${cfg.color}15` }}
                      >
                        {cfg.label}
                      </span>
                      <span className="text-[11px] font-mono text-gray-300 truncate">{log.target}</span>
                    </div>
                    <div className="text-[10px] font-mono text-gray-500 mt-0.5 break-all">{log.detail}</div>
                    <div className="text-[10px] font-mono text-gray-600 mt-0.5">
                      {log.operator} · {formatDateTime(log.timestamp)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ===== 角色创建/编辑弹窗 ===== */}
      <AnimatePresence>
        {showRoleModal && (
          <RoleModal
            role={editingRole}
            roles={config.roles}
            me={me}
            onClose={() => {
              setShowRoleModal(false);
              setEditingRole(null);
            }}
            onSubmit={editingRole ? handleEditRole : handleCreateRole}
          />
        )}
      </AnimatePresence>

      {/* ===== 权限申请弹窗 ===== */}
      <AnimatePresence>
        {showRequestModal && (
          <RequestModal
            roles={config.roles}
            me={me}
            meUsername={meUsername}
            onClose={() => setShowRequestModal(false)}
            onSubmit={handleCreateRequest}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
